import { NgTemplateOutlet } from '@angular/common';
import { booleanAttribute, Component, computed, contentChild, inject, input, output, viewChild } from '@angular/core';

import { MibCheckTruncateDirective } from '../../directives';
import { isFocusVisible } from '../../internal/focus.utils';
import { MIB_THEME_FILL, MibBadgeDirective } from '../badge';
import { MIB_CARD_SURFACE } from '../card';
import { MibIconComponent } from '../icons';
import { MibMenuComponent, MibMenuTriggerDirective } from '../menu';
import { MibThumbnailBusyIcon, MibThumbnailComponent } from '../thumbnail';
import { MibTooltipDirective } from '../tooltip';
import {
  MIB_CASE_FLAG_INK,
  MIB_CASE_MAX_CATEGORIES,
  MIB_CASE_UNKNOWN_CATEGORY,
  MIB_CASE_VOCABULARY,
  MibCase,
  MibCaseBadge,
  mibCaseDuration,
  MibCaseFlag,
  MibCaseFlagBadge,
} from './case-card.models';

let nextShareReasonId = 0;

const HOVER_RING =
  'transition-shadow hover:shadow-[inset_0_0_0_1px_var(--color-mib-branding-300)] hover:duration-0 ' +
  'motion-reduce:transition-none';

/**
 * One case in a list of cases: its picture with the length in a corner, its kinds and states over
 * the picture, and under it the title, a line about the case and who made it when.
 *
 * ```html
 * <mib-case-card [case]="c" (favouriteToggled)="favourite(c, $event)" (opened)="open(c, $event)">
 *   <mib-menu [menuItems]="actions()" align="end" (itemSelected)="run(c, $event)" />
 *
 *   <p>{{ c.procedureName }}</p>
 *   <p>{{ c.operator }} · {{ mibCaseDate(c.at) }}</p>
 *   <p>{{ c.videos }} videos <span mibBadge palette="grey" mode="subtle">IVUS</span></p>
 * </mib-case-card>
 * ```
 *
 * **With an `href`, the whole card is one link**, the title's, stretched over the card: it opens in
 * a new tab and shows its URL the way any link does. The favourite, the share button and the menu sit
 * above it and stay pressable. Without one, the title is plain text and nothing opens the case.
 *
 * **Every part is optional.** What the page does not hand over is not drawn, and the card keeps its
 * shape — a picture and a title alone are a correct card.
 *
 * **It says nothing about where it goes.** No width, no margin: the page listing the cards decides
 * the columns and the gaps.
 *
 * **The menu is the page's.** A `<mib-menu>` written inside the card is opened by a disc the card
 * draws; write none and there is no disc. What is in it — and who is allowed what — is the page's
 * business, which the card never learns. It draws nothing where it is written, so write it first,
 * where its disc is: only the lines are read in the order they come.
 *
 * **So is everything under the title.** The card draws the picture and what is laid over it; what
 * the page writes inside it, bar the menu, lands under the title and **every element is one line**,
 * drawn the same way. A procedure name, who and when, a count of videos, a tag — those grow with a
 * product, and a field per fact is a race the library loses; so would be a slot per line, or a tag
 * to write around each. {@link mibCaseDate} is exported so the date at least stays one date.
 *
 * The title is not one of them: it is the link's own name, so it stays an input and the card writes
 * it inside the anchor itself.
 */
@Component({
  selector: 'mib-case-card',
  exportAs: 'mibCaseCard',
  imports: [
    MibBadgeDirective,
    MibCheckTruncateDirective,
    MibIconComponent,
    MibMenuTriggerDirective,
    MibThumbnailComponent,
    MibTooltipDirective,
    NgTemplateOutlet,
  ],
  templateUrl: './case-card.component.html',
  host: {
    class: MIB_CARD_SURFACE + ' relative block overflow-hidden',
    '[class]': 'hoverClasses()',
  },
})
export class MibCaseCardComponent {
  readonly case = input.required<MibCase>();

  /**
   * Whether the picture is loaded first, for a card the reader sees before scrolling. Set it on the
   * first row of a grid and nowhere else: `NgOptimizedImage` preloads a priority image, and marking
   * them all defeats the point. Unset on a card above the fold, it says so in the console.
   *
   * Decided when the picture is drawn: a change afterwards redraws it rather than loading it twice.
   */
  readonly priority = input(false, { transform: booleanAttribute });

  /** The mark drawn while the case is preparing; left out, the thumbnail's default, a spinner at the card's size. */
  readonly busyIcon = input<MibThumbnailBusyIcon | null>(null);

  /** What the star is named when the case is not a favourite. */
  readonly addFavouriteLabel = input('Add to favourites');

  /** What the star is named when the case is a favourite. */
  readonly removeFavouriteLabel = input('Remove from favourites');

  /** What the share button is named, disabled or not. */
  readonly shareLabel = input('Share');

  /** What the disc that opens the menu is named. */
  readonly actionsLabel = input('Case actions');

  /** The word on the badge of a case still being recorded. */
  readonly liveLabel = input('Live');

  /** The reader pressed the star. Carries the value asked for; the page persists it and hands the case back. */
  readonly favouriteToggled = output<boolean>();

  /** The reader pressed the share button while it was enabled. The page shares the case. */
  readonly shared = output<void>();

  /**
   * The reader pressed the card with a plain left click. A modified click — ctrl, cmd, shift, middle —
   * is left to the browser, which is how a new tab opens.
   *
   * The link keeps its `href` either way. A page that routes in place calls `preventDefault()` on the
   * event and navigates itself; a page that does not lets the browser follow the link. Never emitted
   * for a case without `href`.
   */
  readonly opened = output<MouseEvent>();

  /**
   * The reader pressed the card with ctrl or cmd, asking for it beside the page rather than instead of
   * it. Nothing is prevented: a page that keeps it for itself calls `preventDefault()`, and a page
   * that does not listen leaves the browser to open a tab. Never emitted for a case without `href`.
   */
  readonly openedAside = output<MouseEvent>();

  /** The page's menu, projected. Present, the card draws the disc that opens it; absent, no disc. */
  protected readonly menu = contentChild(MibMenuComponent);

  private readonly vocabulary = inject(MIB_CASE_VOCABULARY);

  protected readonly shareReasonId = `mib-case-card-share-reason-${nextShareReasonId++}`;

  /* An inset ring inside the divider, not a recoloured border; only a card that opens something takes it. */
  protected readonly hoverClasses = computed(() => (this.case().href ? HOVER_RING : ''));

  /*
   Each category written as the page sent it, on the ramp its word was given; an undeclared one on
   the grey ramp. Capped, because the stack is drawn on the picture and a fifth badge would be drawn
   over the case rather than on it — see MIB_CASE_MAX_CATEGORIES.
  */
  protected readonly badges = computed<MibCaseBadge[]>(() =>
    (this.case().categories ?? []).slice(0, MIB_CASE_MAX_CATEGORIES).map((label) => ({
      label,
      palette: (this.vocabulary.categories[label.trim().toLowerCase()] ?? MIB_CASE_UNKNOWN_CATEGORY).palette,
    })),
  );

  /* A word is drawn only if the vocabulary knows it. Kept while preparing: they are raised above the wash. */
  protected readonly flags = computed<MibCaseFlag[]>(() =>
    (this.case().flags ?? [])
      .map((flag) => (typeof flag === 'string' ? this.vocabulary.flags[flag.trim().toLowerCase()] : flag))
      .filter((flag): flag is MibCaseFlag => flag !== undefined),
  );

  protected readonly duration = computed(() => {
    const { preparing, duration } = this.case();
    return preparing || duration === undefined ? '' : mibCaseDuration(duration);
  });

  protected toggleFavourite(): void {
    this.favouriteToggled.emit(!this.case().favourite);
  }

  protected shareCase(): void {
    if (this.case().share?.disabled) return;
    this.shared.emit();
  }

  /* The tooltip sits on the title's words, which never take focus: the link opens it from the keyboard. */
  protected readonly titleTooltip = viewChild('titleTooltip', { read: MibTooltipDirective });

  protected showTitleOnFocus(event: FocusEvent): void {
    if (event.target instanceof Element && isFocusVisible(event.target)) this.titleTooltip()?.show();
  }

  protected open(event: MouseEvent): void {
    if (event.button !== 0 || event.shiftKey || event.altKey) return;
    if (event.ctrlKey || event.metaKey) this.openedAside.emit(event);
    else this.opened.emit(event);
  }

  /*
   A control on a picture: the smallest control height of the scale, on a translucent dark disc so it
   reads on any still. The ring is white for the same reason — the action colour at half strength
   disappears on a dark video frame.
  */
  /* The 28px step of the icon scale, which is what a disc holding a 16px glyph asks for. */
  protected readonly discClasses =
    'grid size-7 cursor-pointer place-items-center rounded-full bg-gray-900/55 text-white ' +
    'hover:bg-gray-900/75 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white';

  /* Disabled through aria-disabled, not `disabled`: a disabled button takes no pointer, so its tooltip never opens. */
  protected readonly shareClasses =
    this.discClasses + ' aria-disabled:cursor-not-allowed aria-disabled:opacity-40 aria-disabled:hover:bg-gray-900/55';

  /* Filled either way: the colour, not the fill, says whether the star is claimed. */
  protected readonly starClasses = computed(
    () => 'size-4 ' + (this.case().favourite ? 'text-mib-favourite' : 'text-white'),
  );

  /* A flag is read, not pressed: the icon step of the scale on a white disc, widened into a pill by its word. */
  /* The 24px step of the icon scale, which is what a disc holding a 16px glyph asks for. */
  protected flagClasses(flag: MibCaseFlag): string {
    return `relative grid size-6 place-items-center rounded-full bg-white ${MIB_CASE_FLAG_INK[flag.theme]}`;
  }

  /* A 16px filled circle holding a size-3 glyph, cut from the disc by a white ring-1. */
  protected flagBadgeClasses(badge: MibCaseFlagBadge): string {
    const fill = MIB_THEME_FILL[badge.theme];
    return `absolute -right-1 -bottom-1 grid size-4 place-items-center rounded-full ring-1 ring-white ${fill.bg} ${fill.text}`;
  }

  /* A value, not a label: the metadata size and not the micro step, which Typography keeps for capitals. */
  protected readonly durationClasses =
    'absolute right-2 bottom-2 flex h-6 items-center rounded-mib bg-gray-900/85 px-2 ' +
    'text-xs/4.5 font-medium text-white tabular-nums';

  /*
   The link is stretched over the card by its `::after`, so a press anywhere is a press on the title.
   Its ring is drawn on that same box, which is the card's, and not around the words.
  */
  protected readonly detailsClasses = 'flex flex-col gap-1 p-4';

  /*
   One line of metadata each, laid out as a row that wraps so a badge sits beside the words rather
   than under them. Empty, the box has no height and costs the column its gap and nothing else.

   A mark in a line is **pinned**, which is the second rule here. `mibBadge` is sized in `em` so it
   takes the size of the text it sits in, and a line is 12px: left alone the mark comes out at 9px,
   under the floor Typography sets, with the icon beside it twice its cap height. Pinned, it is the
   metadata size at 600 in a 24px pill — the mark reads as loud as the words it qualifies. The skin
   stays the badge's: only the geometry is taken over, and a descendant selector outranks the
   directive's own class whatever order the sheet ends up in.
  */
  protected readonly linesClasses =
    'flex flex-col gap-1 text-xs/4.5 text-pretty text-gray-500 ' +
    '[&>*]:flex [&>*]:flex-wrap [&>*]:items-center [&>*]:gap-2 ' +
    '[&_[mibBadge]]:h-6 [&_[mibBadge]]:min-w-6 [&_[mibBadge]]:gap-1 ' +
    '[&_[mibBadge]]:px-2 [&_[mibBadge]]:text-xs [&_[mibBadge]]:font-semibold';

  /* The title's type, link or not; only the link stretches over the card. */
  protected readonly headingClasses = 'text-base font-semibold text-mib-branding-900';

  protected readonly linkClasses =
    this.headingClasses +
    ' after:absolute after:inset-0 ' +
    'focus-visible:outline-none focus-visible:after:outline-2 focus-visible:after:-outline-offset-2 ' +
    'focus-visible:after:outline-mib-primary/50';

  /* One line so every card keeps its height; raised over the link's ::after so only the words open the tooltip. */
  protected readonly titleClasses = 'relative z-1 block truncate';
}
